import type { AcrValue } from 'AuthSdk/oidc-client';

export type AuthErrorKind = 'oauth' | 'step_up_required' | 'refresh_reuse' | 'token_expired' | 'token_invalid' | 'network' | 'jwks' | 'config';

export type TokenInvalidReason = 'signature' | 'issuer' | 'audience' | 'nonce' | 'typ' | 'scope' | 'alg';

export abstract class AuthError extends Error {
  abstract readonly kind: AuthErrorKind;
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = new.target.name;
  }
}

export class OAuthError extends AuthError {
  readonly kind: AuthErrorKind = 'oauth';
  constructor(
    readonly code: string,
    readonly description?: string,
    cause?: unknown,
  ) {
    super(description ? `${code}: ${description}` : code, cause);
  }
}

export class StepUpRequiredError extends OAuthError {
  override readonly kind = 'step_up_required';
  constructor(
    readonly requiredAcr: AcrValue[],
    description?: string,
  ) {
    super('step_up_required', description);
  }
}

export class RefreshReuseError extends OAuthError {
  override readonly kind = 'refresh_reuse';
  constructor(description?: string) {
    super('invalid_grant', description);
  }
}

export class TokenExpiredError extends AuthError {
  readonly kind = 'token_expired';
  constructor(readonly expiredAt: number) {
    super(`token expired at ${expiredAt}`);
  }
}

export class TokenInvalidError extends AuthError {
  readonly kind = 'token_invalid';
  constructor(
    readonly reason: TokenInvalidReason,
    cause?: unknown,
  ) {
    super(`token invalid: ${reason}`, cause);
  }
}

export class NetworkError extends AuthError {
  readonly kind = 'network';
  constructor(
    readonly retryable: boolean,
    cause?: unknown,
  ) {
    super('network error', cause);
  }
}

export class JwksError extends AuthError {
  readonly kind = 'jwks';
  constructor(cause?: unknown) {
    super('jwks error', cause);
  }
}

export class ConfigError extends AuthError {
  readonly kind = 'config';
  constructor(readonly field: string) {
    super(`invalid config: ${field}`);
  }
}
